import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ManualOverride } from '@diamond/shared/types';
import { TextField } from '@diamond/shared/ui';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { useEffect, useState } from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';

interface ManualFallbackFieldsProps {
  fieldIndex: number;
  showWarning?: boolean;
  warningMessage?: string;
}

const formatManualAmount = (value: string): string => {
  const normalized = value.replace(/\D/g, '');
  if (normalized === '') return '';

  return `Rp${Number(normalized).toLocaleString('id-ID')}`;
};

export const ManualFallbackFields = ({
  fieldIndex,
  showWarning = true,
  warningMessage = 'Gambar tidak terbaca. Unggah ulang dengan kualitas yang lebih baik.',
}: ManualFallbackFieldsProps) => {
  const {
    control,
    getValues,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<{ manual_overrides: ManualOverride[] }>();

  const bankFieldName = `manual_overrides.${fieldIndex}.sender_bank` as const;
  const nameFieldName =
    `manual_overrides.${fieldIndex}.sender_account_name` as const;
  const amountFieldName = `manual_overrides.${fieldIndex}.amount` as const;
  const [displayAmount, setDisplayAmount] = useState(() => {
    const currentAmount = getValues(amountFieldName);
    if (typeof currentAmount !== 'number') return '';

    return formatManualAmount(String(currentAmount));
  });
  const amountValue = useWatch({
    control,
    name: amountFieldName,
  });

  useEffect(() => {
    if (typeof amountValue !== 'number') {
      setDisplayAmount('');
      return;
    }

    setDisplayAmount(formatManualAmount(String(amountValue)));
  }, [amountValue]);

  return (
    <Stack spacing={3}>
      {showWarning && (
        <Flex
          align="flex-start"
          gap="8px"
          bg="#FEF2F2"
          borderRadius="4px"
          minH="52px"
          px="8px"
          py="8px"
        >
          <Icon
            as={ErrorOutline}
            color="#FF0000"
            boxSize="20px"
            flexShrink={0}
            mt="8px"
          />
          <Text
            color="#FF0000"
            fontSize="10px"
            fontWeight={500}
            lineHeight="12px"
          >
            {warningMessage}
          </Text>
        </Flex>
      )}

      <Box maxW="166px">
        <FormControl isInvalid={!!get(errors, amountFieldName)}>
          <FormLabel
            aria-label="Nominal"
            color="#373f41"
            fontSize="14px"
            fontWeight={500}
            lineHeight="17px"
            mb="8px"
            whiteSpace="nowrap"
          >
            Nominal
          </FormLabel>
          <Input
            name={amountFieldName}
            aria-label="Nominal"
            placeholder="Tulis disini"
            value={displayAmount}
            onChange={(event) => {
              const normalized = event.target.value.replace(/\D/g, '');
              setDisplayAmount(formatManualAmount(normalized));
              setValue(
                amountFieldName,
                normalized === '' ? undefined : Number(normalized),
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              );
            }}
            bg="white"
            border="1px solid"
            borderColor="gray.33"
            borderRadius="8px"
            fontSize="16px"
            h="40px"
            inputMode="numeric"
            lineHeight="24px"
          />
          <FormErrorMessage textColor="red">
            {get(errors, amountFieldName)?.message as string}
          </FormErrorMessage>
        </FormControl>
      </Box>

      <Box maxW="166px">
        <TextField
          name={bankFieldName}
          label="Nama Bank Pemilik"
          register={register}
          errors={errors}
          placeholder="Tulis disini"
          bg="white"
          border="1px solid"
          borderColor="gray.33"
          borderRadius="8px"
          fontSize="16px"
          h="40px"
          lineHeight="24px"
          maxLength={100}
          labelProps={{
            color: '#373f41',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
            mb: '8px',
            whiteSpace: 'nowrap',
          }}
        />
      </Box>

      <Box maxW="166px">
        <TextField
          name={nameFieldName}
          label="Nama Pemilik Rekening"
          register={register}
          errors={errors}
          placeholder="Tulis disini"
          bg="white"
          border="1px solid"
          borderColor="gray.33"
          borderRadius="8px"
          fontSize="16px"
          h="40px"
          lineHeight="24px"
          maxLength={100}
          labelProps={{
            color: '#373f41',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
            mb: '8px',
            whiteSpace: 'nowrap',
          }}
        />
      </Box>
    </Stack>
  );
};
